// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMModel } from '@form/model';
import { chunk } from 'lodash-es';
import { LMFieldLayoutService, LMLayoutType } from '@field/services/index';
import { AppConstants } from '@app/utils';
import {LMWebAppService} from "@services/lm-webapp.service";

@Injectable()
export class LMFormLayoutService {

  constructor(
    private lmWebAppService: LMWebAppService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmFieldLayoutService: LMFieldLayoutService) {}

  /**
   * Check if section has a visible number label
   * Check to see if all fields have "no label"
   * Evaluate section to see if all fields are display as text (summary form)
   */
  public getSectionInfo(section: LMFormLayoutSectionUI, model: LMModel): LMFormSectionInfo {
    let numberOfFieldsInSection = 0;
    let numberOfDisplayAsTextFields = 0;
    let numberOfFieldsWithNoLabel = 0;
    let numberOfCheckBoxFields = 0;
    let numberOfFormText = 0;
    let numberOfHeadersInSection = 0;
    let hasNumberLabel = false;
    let paragraphHasTextAreaOnly = false;
    let paragraphHasRichTextOnly = false;

    if (section.columns) {
      section.columns.forEach((column) => {
        processElements(this.lmConditionUtilService, column.elements, model);
      });
    }

    if (section.paragraph?.elements) {
      const elements = section.paragraph.elements;
      const nbrElements = elements.length;
      const element = elements[ 0 ];

      if (element !== undefined && element.field !== undefined) {
        const isMultiLineTextArea = element.field?.type === 'textarea' && (element.field.lines !== undefined && element.field.lines > 1);
        const elementIsRichTextArea = element.field?.type === 'richText';

        paragraphHasTextAreaOnly = nbrElements === 1 && isMultiLineTextArea;
        paragraphHasRichTextOnly = nbrElements === 1 && elementIsRichTextArea;
      }

      processElements(this.lmConditionUtilService, section.paragraph.elements, model);
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    function processElements(lmConditionUtilService: LMConditionUtilService, elements: LMFormLayoutElements[], _model: LMModel) {
      elements.forEach((element) => {
        const keys = Object.keys(element);

        if (keys.length === 0) {
          return;
        }

        const field: LMFieldLayout = element[keys[0]];
        const isVisible = lmConditionUtilService.isValid(field.visibility, _model);

        if (field === undefined) {
          return;
        }

        // Nested paragraph
        if (((field as LMFormLayoutParagraph)).elements) {
          processElements(lmConditionUtilService, ((field as LMFormLayoutParagraph)).elements, _model);
        } else {
          if (isVisible) {
            numberOfFieldsInSection++;

            if (field.numberLabel) {
              hasNumberLabel = true;
            }

            if (field.header) {
              numberOfHeadersInSection++;
            }


            // Count buttons, and formtext as fields with no label
            const label = field.label as LMLabelLayout;
            if ((label && label.nolabel)
              || (label === undefined && field.text)
              || (label === undefined && ((field as LMButtonLayout)).displayAsLink)
              || field.displayImageAs && !label.isExplicitLabel
              || (label === undefined && field.type === AppConstants.FORMBUTTON)
              || (label === undefined && field.type === 'action')
              || (label === undefined && field.type === 'navigation')
              || (label === undefined && field.type === 'httpurl')
              || (label === undefined && field.tagStyle)
              || field.type === 'checkbox' && (!field.displayAsTag && field.label)
            ) {
              numberOfFieldsWithNoLabel++;
            }

            // Include the following fields as display as text
            if (
              field.displayAsText
              || field.displayImageAs && !label.isExplicitLabel
              || field.type === 'formtext'
              || ((field as LMButtonLayout)).displayAsLink
              || field.type === 'progress'
              || field.type === 'forminvoke'
              || ((field as LMBlankLineLayout)).blankLine
              || field.tagStyle || field.displayAsTag
              || field.type === 'action'
              || field.type === AppConstants.FORMBUTTON
              || field.type === 'navigation'
              || field.type === 'httpurl'
            ) {
              numberOfDisplayAsTextFields++;
            }

            if (field.type === 'formtext') {
              numberOfFormText++;
            }

            if (field.type === 'checkbox') {
              numberOfCheckBoxFields++;
            }
          }
        }
      });
    }

    return {
      paragraphHasTextAreaOnly,
      paragraphHasRichTextOnly,
      sectionHasNumberLabel: hasNumberLabel,
      isCheckboxesAndFormText: (numberOfFieldsInSection === (numberOfFormText + numberOfCheckBoxFields))  && numberOfFieldsInSection !== 0,
      isSummaryFormSection: (numberOfFieldsInSection === numberOfDisplayAsTextFields) && numberOfFieldsInSection !== 0,
      isNoLabelSection: this.isNoLabelSection(
        numberOfFieldsInSection,
        numberOfFieldsWithNoLabel,
        numberOfCheckBoxFields,
        numberOfHeadersInSection,
        model
      )
    };
  }

  /**
   * Check to see if there is a "VISIBLE" number label present anywhere in the form
   * @param sections
   * @param model
   */
  public formHasNumberLabel(sections: LMFormLayoutSection[], model: LMModel): boolean {
    let hasNumberLabel = false;

    sections.forEach((section) => {
      if (section.columns) {
        section.columns.forEach((column) => {
          processElements(this.lmConditionUtilService, column.elements, model);
        });
      }

      if (section.paragraph && section.paragraph.elements) {
        processElements(this.lmConditionUtilService, section.paragraph.elements, model);
      }

      // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
      function processElements(lmConditionUtilService: LMConditionUtilService, elements: LMFormLayoutElements[], _model: LMModel) {
        elements.forEach((element) => {
          const keys = Object.keys(element);

          if (keys.length === 0) {
            return;
          }

          const field = element[keys[0]];
          const isVisible = lmConditionUtilService.isValid(field.visibility, _model);

          if (field === undefined) {
            return;
          }

          // Nested paragraph
          if (field.elements) {
            processElements(lmConditionUtilService, field.elements, _model);
          } else {
            if (isVisible) {
              if (field.numberLabel) {
                hasNumberLabel = true;
              }
            }
          }
        });
      }
    });

    return hasNumberLabel;
  }

  /**
   * @param numberOfFieldsInSection
   * @param numberOfFieldsWithNoLabel
   * @param numberOfCheckBoxFields
   * @private
   *
   * Implicitly hide labels in a section in certain use cases
   * - All fields have no label (marked as "no label") in LPL
   * - All fields are checkbox fields
   * - All fields are either checkbox fields or "text of" fields
   */
  private isNoLabelSection(
    numberOfFieldsInSection: number,
    numberOfFieldsWithNoLabel: number,
    numberOfCheckBoxFields: number,
    numberOfHeadersInSection: number,
    model: LMModel
  ): boolean {
    // Exclude headers from total number of fields
    numberOfFieldsInSection = numberOfFieldsInSection - numberOfHeadersInSection;

    if ((numberOfFieldsInSection === numberOfFieldsWithNoLabel) && numberOfFieldsInSection !== 0) {
      if (numberOfCheckBoxFields > 0) {
        // on search forms, checkbox fields appear as dropdowns, don't mark the section as no label
        return !model.isSearchForm;
      }
      return  true;
    }

    if ((numberOfFieldsInSection === numberOfCheckBoxFields) && numberOfFieldsInSection !== 0) {
      // on search forms, checkbox fields appear as dropdowns, don't mark the section as no label
      return !model.isSearchForm;
    }

    if (numberOfFieldsInSection === numberOfFieldsWithNoLabel && numberOfFieldsInSection !== 0) {
      if (numberOfCheckBoxFields > 0) {
        // on search forms, checkbox fields appear as dropdowns, don't mark the section as no label
        return !model.isSearchForm;
      }
      return true;
    }

    return  false;
  }

  /**
   *  Build implicit columns array
   */
  public buildImplicitColumns(section: LMFormLayoutSection, model: LMModel): Array<LMFormLayoutColumn> {
    const implicitColumn = section.columns[ 0 ];
    const columns = [];

    for (let i = 0, l = section.nbrColumns; i < l; i++) {
      columns.push({ elements: [], index: i });
    }

    const visibleElements = implicitColumn.elements.filter((element) => {
      let bool = true;
      const key = Object.keys(element)[0];

      if (element[key] && element[key].visibility) {
        bool = this.lmConditionUtilService.isValid( element[key].visibility, model);
      }

      if (bool && 'menu' === key && element[key]) {
        bool = !this.lmWebAppService.isPageMenuItemInaccessible(element[key]);
      }

      return bool;
    });

    const chunks = chunk(visibleElements, Math.ceil(visibleElements.length / section.nbrColumns));
    columns.forEach((column, index) => {
      column.elements = chunks[ index ];
    });

    return columns;
  }

  /**
   * Check if column layout is implicit
   */
  public columnsAreImplicit(section: LMFormLayoutSection): boolean {
    if (section.columns === undefined) {
      return false;
    }

    return section.nbrColumns > 1 && section.columns.length === 1;
  }

  public paragraphHasVisibleLabels(paragraph: LMFormParagraphLayout, model: LMModel, specFields: LMSpecFields, paragraphLayout: LMParagraphLayoutInfo): boolean {
    if (paragraph === undefined
      || (paragraph.elements === undefined && paragraph.leftElements === undefined && paragraph.rightElements === undefined)) {
      return false;
    }

    const elements = [];

    if (paragraph.elements) {
      paragraph.elements.forEach((element) => elements.push(element));
    }

    if (paragraph.leftElements) {
      paragraph.leftElements.forEach((element) => elements.push(element));
    }

    if (paragraph.rightElements) {
      paragraph.rightElements.forEach((element) => elements.push(element));
    }

    const elementsWithLabels = elements.filter((el) => {
      if (el && el.field) {
        const field: LMFieldLayout = el.field;
        const specField = specFields[ field.bindId ];
        const layoutType = this.lmFieldLayoutService.getLayoutType(field, specField, model, paragraphLayout);

        if (layoutType === LMLayoutType.CHECKBOX || (!field.displayAsTag && field.label === undefined)) {
          return false;
        }
      }

      if (el && el.text && el?.text?.type === 'formtext') {
        return false;
      }

      return el[ Object.keys(el)[ 0 ] ].label;
    });

    return elementsWithLabels.length !== 0;
  }

  public paragraphHasRequiredLabels(paragraph: LMFormParagraphLayout, model: LMModel): boolean {
    if (paragraph === undefined || paragraph.elements === undefined) {
      return false;
    }

    const requiredLabels = paragraph.elements.filter((el) => {
      if (el && el.field) {
        if(!(model.getData()?.fields)) {
          return;
        }

        const field: LMField = model.getData().fields[el.field.bindId];

        if (el?.field?.required && field?.mutable) {
          return el;
        }

        return false;
      }
    });

    return requiredLabels.length !== 0;
  }

  public calculatePreviousHeaderSiblings(sections: Array<any>, layoutType: number): void {
    const length = sections.length;
    if (length > 1) {
      for (let index = 1; index < length; index++) {
        const previousSection = sections[index - 1];
        if (previousSection?.sectionLayout === layoutType) {
          sections[index].previousSiblingIsCollapsibleHeader = !!previousSection.header?.collapsible;
        }
      }
    }
  }

  public calculateGridPreviousHeaderSiblings(gridRows: Array<LMFormGridLayoutRow>): void {
    gridRows.forEach((row) => {
      const length = row.elements.length;
      if (length > 1) {
        for (let index = 1; index < length; index++) {
          const previousSection = row[index - 1];
          if (!!previousSection?.header) {
            row[index].previousSiblingIsCollapsibleHeader = previousSection.header.collapsible;
          }
        }
      }
    });
  }
}
